// Shared utility functions for Budget components
// This file centralizes common utility functions to avoid duplication

import moment from 'moment';
import { change } from 'redux-form';

/**
 * Cleans a number input to allow only numbers with up to 2 decimal places
 * @param {string|number} value - The input value to clean
 * @returns {string} - Cleaned number string
 */
export function cleanNumber(value) {
  if (value) {
    return value.toString().match(/^[0-9]*(?:\.\d{0,2})?/)[0];
  }
  return value;
}

/**
 * Cleans a name input to allow only alphanumeric, spaces, and hyphens
 * @param {string} value - The input value to clean
 * @returns {string} - Cleaned name string
 */
export function cleanName(value) {
  if (value) {
    return value.toString().replace(/[^\-0-9A-Za-z ]/, '').substring(0, 255);
  }
  return value;
}

/**
 * Formats a number to 2 decimal places
 * @param {number} value - The value to format
 * @returns {string} - Formatted number string
 */
export function initFormatTwoPlaces(value) {
  if (value) {
    return (((value || 0) * 100) / 100).toFixed(2);
  }
  return value;
}

/**
 * Cleans CSV input from textarea, handling newlines and spaces
 * @param {string} value - The CSV input value
 * @returns {string} - Cleaned CSV string
 */
export function itemCsvInput(value) {
  if (value) {
    return value
      .toString()
      .trim() // remove leading/trailing whitespace
      .replace(/\r/g, '') // remove carriage returns
      .replace(/\s*,\s*/g, ',') // remove space around commas
      .replace(/\n+/g, ',');
  }
  return value;
}

/**
 * Reads active days CSV and returns readable day names
 * @param {string} activeDaysCsv - CSV string of 1s and 0s
 * @returns {string} - Comma-separated day names
 */
export function readableActiveDays(activeDaysCsv) {
  const readableArray = [];
  const activeDaysArray = activeDaysCsv?.split(',') || [];
  activeDaysArray.forEach((day, pos) => {
    if (day === '1') {
      readableArray.push(moment().isoWeekday(pos + 1).format('dddd'));
    }
  });
  return readableArray.join(', ');
}

/**
 * Counts active days in the current month based on CSV and date range
 * @param {string} activeDaysCsv - CSV string of 1s and 0s
 * @param {string} startDate - Start date
 * @param {string} endDate - End date (can be null)
 * @returns {number} - Count of active days
 */
export function countActiveDaysInMonth(activeDaysCsv, startDate, endDate) {
  let count = 0;
  const today = moment();
  const daysInMonth = today.daysInMonth();
  for (let day = 1; day <= daysInMonth; day += 1) {
    const checkDay = moment([today.year(), today.month(), day]);
    const dayOfWeekNum = checkDay.isoWeekday() - 1;
    if (activeDaysCsv.split(',')[dayOfWeekNum] === '1' && checkDay >= moment(startDate) && (checkDay <= moment(endDate) || endDate === null)) {
      count += 1;
    }
  }
  return count;
}

/**
 * Looks up a label from a mapping array by value
 * @param {Array} mappings - Array of {label, value} objects
 * @param {*} valueKey - The value to look up
 * @returns {Object|undefined} - The matching object or undefined
 */
export function lookupValue(mappings, valueKey) {
  return mappings.find(map => map.value === valueKey);
}

/**
 * Initializes a Redux form dropdown field with the correct option
 * @param {Function} dispatch - Redux dispatch function
 * @param {string} formName - Name of the form
 * @param {string} fieldName - Name of the field
 * @param {*} value - Current value
 * @param {Array} options - Array of options
 * @param {number} defaultPosition - Default position if value not found (default 0)
 */
export function initDropdown(dispatch, formName, fieldName, value, options, defaultPosition = 0) {
  if (options) {
    const selectedOption = value || value === ''
      ? options.find(option => option.value === value)
      : options[defaultPosition];
    dispatch(change(formName, fieldName, selectedOption));
  }
}
